<template>
  <div>
    <main
      :class="[
        'min-h-screen bg-background mt-10 lg:pt-24 md:pt-10',
        contentData.theme === 'night' ? 'bg-black' : 'bg-white'
      ]"
      :style="{ backgroundColor: contentData.backgroundColor || '#ffffff' }"
    >
      <div class="">
        <div
          :class="[
            'z-[1] relative grid gap-8 lg:grid-cols-2 lg:gap-16 mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8 transition-colors duration-300',
            contentData.theme === 'night' ? 'text-white' : 'text-black'
          ]"
          :style="{ backgroundColor: contentData.backgroundColor || '#ffffff' }"
        >
          <!-- Product Images -->
          <div class="space-y-4">
            <!-- Main Image -->
            <div 
              :class="[
                'aspect-square border bg-secondary',
                contentData.theme === 'night' ? 'border-white' : 'border-black'
              ]"
            >
              <img
                :src="mainImage"
                :alt="contentData.productTitle || t('pages.sampleProduct.images.mainAlt')"
                class="h-full w-full object-cover"
              />
            </div>

            <!-- Thumbnail Grid -->
            <div class="grid grid-cols-4 gap-4">
              <button
                v-for="(image, index) in currentImages"
                :key="index"
                @click="selectedImage = index"
                :class="[
                  'aspect-square border bg-secondary transition-all',
                  contentData.theme === 'night' 
                    ? (selectedImage === index ? 'border-white border-2' : 'border-white hover:border-2')
                    : (selectedImage === index ? 'border-black border-2' : 'border-black hover:border-2')
                ]"
              >
                <img
                  :src="image"
                  :alt="t('pages.sampleProduct.images.thumbnailAlt', { index: index + 1 })"
                  class="h-full w-full object-cover"
                />
              </button>
            </div>
          </div>

          <!-- Product Info -->
          <div class="flex flex-col">
            <div class="space-y-6">
              <!-- Title & Price -->
              <div class="space-y-2">
                <!-- Brand Name and Status - Brutalist Style -->
                <div class="flex items-center gap-3">
                  <!-- <div class="inline-block">
                    <span 
                      :class="[
                        'inline-block px-3 py-1 text-sm rounded-r-full font-serif lowercase tracking-wider border-2 transform -rotate-1 tracking-widest',
                        contentData.theme === 'night' ? 'bg-white text-black border-white' : 'bg-black text-white border-black'
                      ]"
                    >
                      {{ contentData.brand || 'YourBrand' }}
                    </span>
                  </div> -->
                  <!-- Brutalist Defect Status Indicator -->
                  <span 
                    :class="[
                      'inline-block px-2 py-1 text-xs font-bold uppercase tracking-wider border-2 transform rotate-1',
                      contentData.theme === 'night' ? 'bg-white text-black border-white' : 'bg-black text-yellow-400 border-black'
                    ]"
                  >
                    {{ getStatusDisplay(contentData.status) }}
                  </span>
                </div>

                <h2
                  :class="[
                      'text-3xl tracking-tight text-balance font-bold',
                      contentData.theme === 'night' ? 'text-white' : 'text-black'
                    ]">
                  {{ displayBrand }}
                </h2>
                <h1 
                  :class="[
                      'text-3xl tracking-tight text-balance',
                      contentData.theme === 'night' ? 'text-white' : 'text-black'
                    ]">
                  {{ displayProductTitle }}
                </h1>
                <p class="text-3xl font-thin tracking-tight text-balance"
                :class="contentData.theme === 'night' ? 'text-white' : 'text-black'">{{ displayPrice }}</p>
              </div>

              <!-- Description -->
              <div :class="['space-y-4 pt-6', borderColor, contentData.theme === 'night' ? 'text-white' : 'text-black']">
                <p class="text-base leading-relaxed max-w-lg">
                  {{ t('pages.sampleProduct.description.primary') }}
                </p>
                <p class="text-base leading-relaxed text-muted-foreground max-w-lg">
                  {{ t('pages.sampleProduct.description.secondary') }}
                </p>
              </div>

              <!-- Size/Model Selection (only show when no Content Studio images) -->
              <div v-if="showModelSelector" :class="['space-y-4 pt-6', borderColor, contentData.theme === 'night' ? 'text-white' : 'text-black']">
                <label class="text-xl lowercase tracking-wider font-serif">{{ t('pages.sampleProduct.model.label') }}</label>
                <div class="flex gap-2">
                  <button
                    v-for="size in sizes"
                    :key="size"
                    @click="selectedSize = size"
                    :class="[
                      'flex h-12 w-12 items-center justify-center border font-medium transition-all',
                      selectedSize === size
                        ? (contentData.theme === 'night' ? 'border-2 border-white bg-white text-black' : 'border-2 border-black bg-black text-white')
                        : (contentData.theme === 'night' ? 'border-white bg-black text-white hover:border-2' : 'border-black bg-background hover:border-2')
                    ]"
                  >
                    {{ size }}
                  </button>
                </div>
              </div>

              <!-- Quantity & Add to Cart -->
              <div :class="['space-y-4 border-t pt-6', borderColor, contentData.theme === 'night' ? 'text-white' : 'text-black']">
                <label class="text-xl lowercase tracking-wider font-serif">{{ t('pages.sampleProduct.quantity.label') }}</label>
                <div class="flex gap-4">
                  <div :class="['flex items-center border', borderColor]">
                    <button
                      @click="handleQuantityChange(-1)"
                      :class="[
                        'flex h-12 w-12 items-center justify-center transition-colors',
                        contentData.theme === 'night' ? 'hover:bg-white hover:text-black' : 'hover:bg-black hover:text-white'
                      ]"
                      :aria-label="t('pages.sampleProduct.quantity.decrease')"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                      </svg>
                    </button>
                    <span :class="['flex h-12 w-16 items-center justify-center border-x font-medium font-mono', borderColor]">
                      {{ quantity }}
                    </span>
                    <button
                      @click="handleQuantityChange(1)"
                      :class="[
                        'flex h-12 w-12 items-center justify-center transition-colors',
                        contentData.theme === 'night' ? 'hover:bg-white hover:text-black' : 'hover:bg-black hover:text-white'
                      ]"
                      :aria-label="t('pages.sampleProduct.quantity.increase')"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  <button
                    :class="[
                      'flex-1 h-12 border-2 tracking-wider transition-colors',
                      contentData.theme === 'night' 
                        ? 'bg-white text-black hover:bg-black hover:text-white border-white' 
                        : 'bg-black text-white hover:bg-white hover:text-black border-black'
                    ]"
                  >
                    {{ t('pages.sampleProduct.actions.addToCart') }}
                  </button>
                </div>
              </div>

              <!-- Accordions: Details, Pack & Ship, Payment -->
              <div class="space-y-0">
                <!-- Accordion 1: Details -->
                <div :class="['border-t', borderColor]">
                  <button
                    @click="accordionOpen.details = !accordionOpen.details"
                    :class="[
                      'w-full flex items-center justify-between py-4 text-left transition-colors',
                      contentData.theme === 'night' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-700'
                    ]"
                  >
                    <div class="flex items-center gap-3">
                      <span class="text-sm font-mono font-bold">01.</span>
                      <h3 class="text-xl lowercase tracking-wider font-serif -mt-1">{{ t('pages.sampleProduct.accordion.details.title') }}</h3>
                    </div>
                    <svg 
                      :class="['w-5 h-5 transition-transform', accordionOpen.details ? 'rotate-180' : '']"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 transform -translate-y-2"
                    enter-to-class="opacity-100 transform translate-y-0"
                    leave-active-class="transition-all duration-300 ease-in"
                    leave-from-class="opacity-100 transform translate-y-0"
                    leave-to-class="opacity-0 transform -translate-y-2"
                  >
                    <div v-show="accordionOpen.details" class="pb-6 pl-8 my-4 pr-11">
                      <ul class="space-y-2 text-sm leading-relaxed">
                        <li class="flex">
                          <span class="w-32 text-muted-foreground">{{ t('pages.sampleProduct.accordion.details.specs.material.label') }}:</span>
                          <span>{{ t('pages.sampleProduct.accordion.details.specs.material.value') }}</span>
                        </li>
                        <li class="flex">
                          <span class="w-32 text-muted-foreground">{{ t('pages.sampleProduct.accordion.details.specs.finish.label') }}:</span>
                          <span>{{ t('pages.sampleProduct.accordion.details.specs.finish.value') }}</span>
                        </li>
                        <li class="flex">
                          <span class="w-32 text-muted-foreground">{{ t('pages.sampleProduct.accordion.details.specs.dimensions.label') }}:</span>
                          <span class="font-mono">{{ t('pages.sampleProduct.accordion.details.specs.dimensions.value') }}</span>
                        </li>
                        <li class="flex">
                          <span class="w-32 text-muted-foreground">{{ t('pages.sampleProduct.accordion.details.specs.origin.label') }}:</span>
                          <span>{{ t('pages.sampleProduct.accordion.details.specs.origin.value') }}</span>
                        </li>
                        <li class="flex">
                          <span class="w-32 text-muted-foreground">{{ t('pages.sampleProduct.accordion.details.specs.care.label') }}:</span>
                          <span>{{ t('pages.sampleProduct.accordion.details.specs.care.value') }}</span>
                        </li>
                      </ul>
                    </div>
                  </transition>
                </div>

                <!-- Accordion 2: Pack & Ship -->
                <div :class="['border-t', borderColor]">
                  <button
                    @click="accordionOpen.shipping = !accordionOpen.shipping"
                    :class="[
                      'w-full flex items-center justify-between py-4 text-left transition-colors',
                      contentData.theme === 'night' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-700'
                    ]"
                  >
                    <div class="flex items-center gap-3">
                      <span class="text-sm font-mono font-bold">02.</span>
                      <h3 class="text-xl lowercase tracking-wider font-serif -mt-1">{{ t('pages.sampleProduct.accordion.shipping.title') }}</h3>
                    </div>
                    <svg 
                      :class="['w-5 h-5 transition-transform', accordionOpen.shipping ? 'rotate-180' : '']"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 transform -translate-y-2"
                    enter-to-class="opacity-100 transform translate-y-0"
                    leave-active-class="transition-all duration-300 ease-in"
                    leave-from-class="opacity-100 transform translate-y-0"
                    leave-to-class="opacity-0 transform -translate-y-2"
                  >
                    <div v-show="accordionOpen.shipping" class="pb-6 space-y-2 text-sm pl-8 my-4 pr-11">
                      <p class="font-medium uppercase tracking-wider">{{ t('pages.sampleProduct.accordion.shipping.tagline') }}</p>
                      <p class="text-muted-foreground">
                        {{ t('pages.sampleProduct.accordion.shipping.body1') }}
                      </p>
                      <p class="text-muted-foreground">
                        {{ t('pages.sampleProduct.accordion.shipping.body2') }}
                      </p>
                    </div>
                  </transition>
                </div>

                <!-- Accordion 3: Payment -->
                <div :class="['border-t', borderColor]">
                  <button
                    @click="accordionOpen.payment = !accordionOpen.payment"
                    :class="[
                      'w-full flex items-center justify-between py-4 text-left transition-colors',
                      contentData.theme === 'night' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-700'
                    ]"
                  >
                    <div class="flex items-center gap-3">
                      <span class="text-sm font-mono font-bold">03.</span>
                      <h3 class="text-xl lowercase tracking-wider -mt-1 font-serif">{{ t('pages.sampleProduct.accordion.payment.title') }}</h3>
                    </div>
                    <svg 
                      :class="['w-5 h-5 transition-transform', accordionOpen.payment ? 'rotate-180' : '']"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 transform -translate-y-2"
                    enter-to-class="opacity-100 transform translate-y-0"
                    leave-active-class="transition-all duration-300 ease-in"
                    leave-from-class="opacity-100 transform translate-y-0"
                    leave-to-class="opacity-0 transform -translate-y-2"
                  >
                    <div v-show="accordionOpen.payment" class="pb-6 space-y-2 text-sm pl-8 my-4 pr-11">
                      <p>{{ t('pages.sampleProduct.accordion.payment.body1') }}</p>
                      <p class="text-muted-foreground">{{ t('pages.sampleProduct.accordion.payment.body2') }}</p>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Curated by Artist Section -->
        <section class="mt-24 border-t border-black pt-16 mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8 z-[1] bg-white relative pt-24 mt-64">
          <div class="mb-12 max-w-3xl">
            <h2 class="mb-6 text-6xl text-black">
              {{ t('pages.sampleProduct.curated.titlePrefix') }}
              <span class="font-serif">{{ displayBrand }}</span><sup class="font-thin">©</sup>
            </h2>
            <p class="text-lg leading-relaxed text-muted-foreground">
              {{ t('pages.sampleProduct.curated.description') }}
            </p>
          </div>
          <div class="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <!-- Show Content Studio items if any exist -->
            <template v-if="contentData.pairedProducts && contentData.pairedProducts.length > 0">
              <button
                v-for="(pair, index) in contentData.pairedProducts.slice(0, 3)"
                :key="index"
                @click="openContentStudio"
                class="text-left bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
              >
                <div class="aspect-[4/3] border-b border-black bg-secondary overflow-hidden">
                  <img
                    v-if="pair.imageUrl"
                    :src="pair.imageUrl"
                    :alt="pair.title || 'Curated Product'"
                    class="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div v-else class="h-full w-full bg-gray-100 flex items-center justify-center text-gray-400">
                    <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                </div>
                <div class="flex flex-col flex-1 p-6">
                  <div class="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.curated.cards.label') }}</p>
                      <h3 class="text-xl font-bold leading-tight">{{ pair.title || t('pages.sampleProduct.curated.cards.defaultTitle') }}</h3>
                    </div>
                    <svg class="h-5 w-5 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <p class="text-sm leading-relaxed text-muted-foreground mb-6">
                    {{ pair.description || t('pages.sampleProduct.curated.cards.defaultDescription') }}
                  </p>
                  <div class="mt-auto flex items-center justify-between border-t border-black pt-4">
                    <span class="text-sm font-medium uppercase tracking-wider">
                      {{ pair.price ? t('pages.sampleProduct.curated.cards.shop') : t('pages.sampleProduct.curated.cards.view') }}
                    </span>
                    <span v-if="pair.price" class="text-sm font-medium">€{{ pair.price }}</span>
                    <span v-else class="text-sm text-muted-foreground">{{ t('pages.sampleProduct.curated.cards.external') }}</span>
                  </div>
                </div>
              </button>
            </template>

            <!-- Default cards when no Content Studio items -->
            <template v-else>
              <!-- Workshop Card -->
              <button
                @click="openContentStudio"
                class="text-left bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
              >
                <div class="aspect-[4/3] border-b border-black bg-secondary overflow-hidden">
                  <SmartImage src="/mocks-image-product-page/ceramic-workshop-hands-working-with-clay.jpg" alt="Ceramic Workshop" class="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </div>
                <div class="flex flex-col flex-1 p-6">
                  <div class="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.curated.fallback.workshop.badge') }}</p>
                      <h3 class="text-xl font-bold leading-tight">{{ t('pages.sampleProduct.curated.fallback.workshop.title') }}</h3>
                    </div>
                    <svg class="h-5 w-5 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <p class="text-sm leading-relaxed text-muted-foreground mb-6">
                    {{ t('pages.sampleProduct.curated.fallback.workshop.body') }}
                  </p>
                  <div class="mt-auto flex items-center justify-between border-t border-black pt-4">
                    <span class="text-sm font-medium uppercase tracking-wider">{{ t('pages.sampleProduct.curated.fallback.workshop.cta') }}</span>
                    <span class="text-sm text-muted-foreground">{{ t('pages.sampleProduct.curated.cards.external') }}</span>
                  </div>
                </div>
              </button>

              <!-- Different Style Product Card -->
              <button
                @click="openContentStudio"
                class="text-left bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
              >
                <div class="aspect-[4/3] border-b border-black bg-secondary overflow-hidden">
                  <SmartImage
                    src="/mocks-image-product-page/colorful-abstract-painting-vibrant.jpg"
                    alt="Abstract Painting"
                    class="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
                <div class="flex flex-col flex-1 p-6">
                  <div class="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.curated.fallback.different.badge') }}</p>
                      <h3 class="text-xl font-bold leading-tight">{{ t('pages.sampleProduct.curated.fallback.different.title') }}</h3>
                    </div>
                    <svg class="h-5 w-5 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <p class="text-sm leading-relaxed text-muted-foreground mb-6">
                    {{ t('pages.sampleProduct.curated.fallback.different.body') }}
                  </p>
                  <div class="mt-auto flex items-center justify-between border-t border-black pt-4">
                    <span class="text-sm font-medium uppercase tracking-wider">{{ t('pages.sampleProduct.curated.fallback.different.cta') }}</span>
                    <span class="text-sm text-muted-foreground">{{ t('pages.sampleProduct.curated.cards.external') }}</span>
                  </div>
                </div>
              </button>

              <!-- Instagram Card -->
              <button
                @click="openContentStudio"
                class="text-left bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
              >
                <div class="aspect-[4/3] border-b border-black bg-secondary overflow-hidden">
                  <SmartImage src="/mocks-image-product-page/artist-studio-workspace-ceramics.jpg" alt="Artist Instagram" class="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </div>
                <div class="flex flex-col flex-1 p-6">
                  <div class="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.curated.fallback.follow.badge') }}</p>
                      <h3 class="text-xl font-bold leading-tight">{{ t('pages.sampleProduct.curated.fallback.follow.title') }}</h3>
                    </div>
                    <svg class="h-5 w-5 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <p class="text-sm leading-relaxed text-muted-foreground mb-6">
                    {{ t('pages.sampleProduct.curated.fallback.follow.body') }}
                  </p>
                  <div class="mt-auto flex items-center justify-between border-t border-black pt-4">
                    <span class="text-sm font-medium uppercase tracking-wider">{{ t('pages.sampleProduct.curated.fallback.follow.cta') }}</span>
                    <span class="text-sm text-muted-foreground">{{ t('pages.sampleProduct.curated.cards.external') }}</span>
                  </div>
                </div>
              </button>
            </template>
          </div>
        </section>

        <!-- Discover More Section -->
        <section class="z-[1] relative mt-24 border-t border-black py-24 bg-yellow-400">
          <div class="mb-12 max-w-3xl mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8">
            <h2 class="mb-6 text-6xl text-black">
              <span class="font-serif lowercase">ommi.shop</span> {{ t('pages.sampleProduct.materials.titleSuffix') }}
            </h2>
            <p class="text-lg leading-relaxed text-muted-foreground">
              {{ t('pages.sampleProduct.materials.description') }}
            </p>
          </div>

          <div class="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8">
            <!-- Woven Textile Card -->
            <button
              @click="openContentStudio"
              class="bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
            >
              <div class="aspect-square border-b border-black bg-secondary overflow-hidden">
                <SmartImage src="/mocks-image-product-page/handwoven-textile-wall-hanging-irregular.jpg" alt="Handwoven Wall Hanging" class="h-full w-full object-cover transition-transform group-hover:scale-105" />
              </div>
              <div class="flex flex-col flex-1 p-6">
                <div class="mb-4">
                  <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.materials.cards.textile.badge') }}</p>
                  <h3 class="text-xl font-bold leading-tight mb-3">{{ t('pages.sampleProduct.materials.cards.textile.title') }}</h3>
                  <p class="text-sm leading-relaxed text-muted-foreground">
                    {{ t('pages.sampleProduct.materials.cards.textile.body') }}
                  </p>
                </div>
                <div class="mt-auto pt-4">
                  <span class="text-sm font-medium uppercase tracking-wider border-b border-black pb-1 inline-block">
                    {{ t('pages.sampleProduct.materials.cards.textile.cta') }}
                  </span>
                </div>
              </div>
            </button>

            <!-- Wooden Bowl Card -->
            <button
              @click="openContentStudio"
              class="bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
            >
              <div class="aspect-square border-b border-black bg-secondary overflow-hidden">
                <SmartImage src="/mocks-image-product-page/hand-carved-wooden-bowl-natural-cracks.jpg" alt="Hand-Carved Wooden Bowl" class="h-full w-full object-cover transition-transform group-hover:scale-105" />
              </div>
              <div class="flex flex-col flex-1 p-6">
                <div class="mb-4">
                  <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.materials.cards.wood.badge') }}</p>
                  <h3 class="text-xl font-bold leading-tight mb-3">{{ t('pages.sampleProduct.materials.cards.wood.title') }}</h3>
                  <p class="text-sm leading-relaxed text-muted-foreground">
                    {{ t('pages.sampleProduct.materials.cards.wood.body') }}
                  </p>
                </div>
                <div class="mt-auto pt-4">
                  <span class="text-sm font-medium uppercase tracking-wider border-b border-black pb-1 inline-block">
                    {{ t('pages.sampleProduct.materials.cards.wood.cta') }}
                  </span>
                </div>
              </div>
            </button>

            <!-- Glass Vessel Card -->
            <button
              @click="openContentStudio"
              class="bg-white group flex flex-col border border-black bg-background transition-all hover:border-2 cursor-pointer"
            >
              <div class="aspect-square border-b border-black bg-secondary overflow-hidden">
                <SmartImage src="/mocks-image-product-page/hand-blown-glass-vessel-bubbles.jpg" alt="Hand-Blown Glass Vessel" class="h-full w-full object-cover transition-transform group-hover:scale-105" />
              </div>
              <div class="flex flex-col flex-1 p-6">
                <div class="mb-4">
                  <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{{ t('pages.sampleProduct.materials.cards.glass.badge') }}</p>
                  <h3 class="text-xl font-bold leading-tight mb-3">{{ t('pages.sampleProduct.materials.cards.glass.title') }}</h3>
                  <p class="text-sm leading-relaxed text-muted-foreground">
                    {{ t('pages.sampleProduct.materials.cards.glass.body') }}
                  </p>
                </div>
                <div class="mt-auto pt-4">
                  <span class="text-sm font-medium uppercase tracking-wider border-b border-black pb-1 inline-block">
                    {{ t('pages.sampleProduct.materials.cards.glass.cta') }}
                  </span>
                </div>
              </div>
            </button>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="border-t border-black py-24 z-[1] bg-white relative">
          <div class="mx-auto max-w-4xl text-center space-y-8">
            <h2 class="text-6xl text-black" v-html="t('pages.sampleProduct.cta.title')"></h2>
            <p class="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              {{ t('pages.sampleProduct.cta.description') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a
                href="/artists"
                class="inline-flex items-center justify-center h-14 px-8 border-2 border-black bg-background text-black hover:bg-black hover:text-white transition-colors font-medium uppercase tracking-wider"
              >
                {{ t('pages.sampleProduct.cta.primary') }}
              </a>
              <a
                href="/join"
                class="inline-flex items-center justify-center h-14 px-8 border-2 border-black bg-black text-white hover:bg-background hover:text-yellow-400 transition-colors font-medium uppercase tracking-wider"
              >
                {{ t('pages.sampleProduct.cta.secondary') }}
              </a>
            </div>
          </div>
        </section>
      </div>
    </main>
    
    <!-- Content Studio Modal -->
    <ContentStudio ref="contentStudio" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { useI18n } from '#imports'
import { useSeo } from '~/composables/useSeo'
import { useContentStudio } from '~/composables/useContentStudio'
import ContentStudio from '~/components/ContentStudio.vue'

definePageMeta({
  layout: 'default'
})

const { t } = useI18n()

useSeo({
  title: t('pages.sampleProduct.meta.title'),
  description: t('pages.sampleProduct.meta.description'),
  keywords: 'ommi.shop sample product, product page, customizable product, imperfect art',
  type: 'product'
})

const { contentData, loadContentData, getStatusDisplay, getStatusColor } = useContentStudio()

// Content Studio modal
const contentStudio = ref<InstanceType<typeof ContentStudio> | null>(null)

// Product page state
const selectedImage = ref(0)
const selectedSize = ref('#3')
const quantity = ref(1)
const sizes = ['#3', '#1', '#2', '#4']
const isContentLoaded = ref(false)

// Accordion states (all start closed)
const accordionOpen = ref({
  payment: false,
  details: false,
  shipping: false
})

// Images by model (matching React example)
const imagesByModel: Record<string, string[]> = {
  '#2': [
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-2-vase-main-view.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-2-vase-texture-detail.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-2-vase-glaze-variation.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-2-vase-base-detail.jpg',
  ],
  '#1': [
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-1-vase-front-view.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-1-vase-side-angle.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-1-vase-top-view.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-1-vase-imperfection-detail.jpg',
  ],
  '#3': [
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-3-vase-primary-view.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-3-vase-surface-texture.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-3-vase-rim-detail.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-3-vase-profile-view.jpg',
  ],
  '#4': [
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-4-vase-main-shot.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-4-vase-glaze-closeup.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-4-vase-form-detail.jpg',
    'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/mocks-image-product-page/model-4-vase-artisan-marks.jpg',
  ],
}

const displayBrand = computed(() => contentData.brand || t('pages.sampleProduct.defaults.brand'))
const displayProductTitle = computed(() => contentData.productTitle || t('pages.sampleProduct.defaults.productTitle'))
const displayPrice = computed(() => {
  const raw = (contentData.price || contentData.productPrice || '').toString().trim()
  if (raw) {
    return raw
  }
  return t('pages.sampleProduct.defaults.price')
})

// Get current images based on selected size
const currentImages = computed<string[]>(() => {
  // If Content Studio has valid images (with non-empty URLs), use those
  if (contentData.productImages && contentData.productImages.length > 0) {
    const validImages = contentData.productImages
      .filter((img: { url: string }) => img.url && img.url.trim() !== '')
      .slice(0, 4)
      .map((img: { url: string }) => img.url)
    
    if (validImages.length > 0) {
      console.log('Using Content Studio images:', validImages)
      return validImages
    }
  }
  // Otherwise use mock images based on selected model (default to #3)
  const fallbackImages = imagesByModel['#3'] ?? []
  const mockImages = imagesByModel[selectedSize.value] ?? fallbackImages
  console.log('Using mock images:', mockImages)
  return mockImages
})

// Check if we should show model selector
const showModelSelector = computed(() => {
  // Show model selector if no Content Studio images OR if all images have empty URLs
  if (!contentData.productImages || contentData.productImages.length === 0) {
    return true
  }
  
  const validImages = contentData.productImages.filter((img: { url: string }) => img.url && img.url.trim() !== '')
  return validImages.length === 0
})

// Get main image
const mainImage = computed(() => {
  const images = currentImages.value
  const mainImg = images[selectedImage.value] ?? images[0] ?? ''
  console.log('Main image:', mainImg, 'Selected index:', selectedImage.value)
  return mainImg
})

// Get border color based on theme
const borderColor = computed(() => {
  return contentData.theme === 'night' ? 'border-white' : 'border-black'
})

// Get button classes based on theme
const buttonClasses = computed(() => {
  return contentData.theme === 'night' 
    ? 'border-white bg-black text-white hover:bg-white hover:text-black'
    : 'border-black bg-background hover:border-2'
})

// Quantity handler
const handleQuantityChange = (delta: number) => {
  quantity.value = Math.max(1, quantity.value + delta)
}

// Open Content Studio modal
const openContentStudio = () => {
  if (contentStudio.value) {
    contentStudio.value.openModal()
  }
}


// Watch for size changes to reset selected image
watch(selectedSize, () => {
  selectedImage.value = 0
})

// Watch for Content Studio images to reset selected image
watch(
  () => contentData.productImages,
  (newImages: { url: string }[]) => {
    if (newImages && newImages.length > 0) {
      const validImages = newImages.filter((img: { url: string }) => img.url && img.url.trim() !== '')
      if (validImages.length > 0) {
        selectedImage.value = 0
      }
    }
  }
)

// Load content data on component mount
onMounted(() => {
  loadContentData()
  isContentLoaded.value = true
  console.log('Content data loaded:', contentData)
  console.log('Product images:', contentData.productImages)
})

// Watch for changes in localStorage and custom events to update the page in real-time
if (import.meta.client) {
  // Listen for localStorage changes (from other tabs)
  window.addEventListener('storage', (e) => {
    if (e.key === 'content-studio-data') {
      loadContentData()
    }
  })
  
  // Listen for real-time updates from Content Studio
  window.addEventListener('content-studio-updated', (e: Event) => {
    // Force reactivity by accessing the data
    const customEvent = e as CustomEvent
    console.log('Content Studio updated:', customEvent.detail)
  })
}
</script>

